import { Building2, Lock, UserRound, X, type LucideIcon } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Skeleton } from "@/components/ui/feedback";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { SearchableFieldPicker } from "@/features/searchable-field-picker";
import { ApiError } from "@/lib/api";
import {
  SEARCHABLE_MAX, alwaysSearched, changedSearchable, entityLabel, fieldTypeLabel, recordNoun,
  replaceSearchable, searchableChoices, searchableKeys, searchableOptions, searchableProblem,
  toggleSearchable,
} from "@/lib/forms";
import { useFormDesign, useSetSearchable } from "@/lib/queries";
import type { FormEntity } from "@/lib/types";

const OBJECTS: readonly FormEntity[] = ["lead", "firm"];
/** The Custom fields page's icons for the two objects, so the tabs read as its object cards do. */
const OBJECT_ICON: Record<FormEntity, LucideIcon> = { lead: UserRound, firm: Building2 };

/** Where each object's searchable fields are searched, under the object tabs. */
const SEARCHED_IN: Record<FormEntity, string> = {
  lead: "The Contacts search, the Opportunities board and every contact picker.",
  firm: "The Firms search and every firm picker.",
};

/**
 * Edit searchable fields (ADR-101): GoHighLevel's window of the same name, drawn as this console's
 * settings panes are, from the right as Create field is. The object is a tab (Contact, Firm), then come
 * the fields its search box looks in. The platform's own fields are listed first, locked, because they are
 * always searched; under them, one row per field an admin added, each changed or removed in place, and a
 * blank row at the end to add another, up to six. A row's picker offers only what can be searched (text,
 * long text, email, phone, dropdown, multi-select) and is not in another row.
 *
 * Each object keeps its own changes while the other is shown, and Save sends every object that changed,
 * each as its whole list. Nothing is shown or sent for an object until its form is read, so a save can
 * never send an empty list for a form that was never loaded. While it saves, the dialog is locked: no
 * edit, no Cancel, no Escape, so what is sent is what was on screen and nothing is sent after a cancel.
 * If a second object fails after the first saved, it says which one was saved.
 *
 * The keyboard keeps its place: a row's field changes in place (rows are keyed by position), and after a
 * row is removed, or the sixth field fills the list, the focus goes to the row now in that place.
 * Opened from the Custom fields page's menu, or from *Customize here* in a search box's hint, on that
 * page's object.
 */
export function SearchableFieldsDialog({
  open, onOpenChange, entity: initialEntity, tenantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The object shown first. */
  entity: FormEntity;
  /** A superadmin's tenant; null for everyone else, whose own it is. */
  tenantId: number | null;
}) {
  const toast = useToast();
  const save = useSetSearchable();
  const lead = useFormDesign("lead", tenantId, { enabled: open });
  const firm = useFormDesign("firm", tenantId, { enabled: open });
  const designs = { lead, firm };
  const forms = { lead: lead.data, firm: firm.data };
  const [entity, setEntity] = React.useState<FormEntity>(initialEntity);
  /** What each object's list was changed to here; an object not in it is as saved. */
  const [drafts, setDrafts] = React.useState<Partial<Record<FormEntity, string[]>>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const objectId = React.useId();
  const rowRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const blankRef = React.useRef<HTMLButtonElement | null>(null);
  /** The row to put the focus on once the list has changed under it; the blank row when there is none. */
  const [focusRow, setFocusRow] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (focusRow === null) return;
    (rowRefs.current[focusRow] ?? blankRef.current)?.focus();
    setFocusRow(null);
  }, [focusRow]);

  const design = designs[entity];
  const form = design.data;
  const picked = drafts[entity] ?? searchableKeys(form);
  const changed = changedSearchable(drafts, forms);
  const choices = new Map(searchableChoices(form).map((c) => [c.field.key, c]));
  const offerable = searchableOptions(form, []).length;
  const full = picked.length >= SEARCHABLE_MAX;
  const noun = recordNoun(entity);
  const setPicked = (next: string[]) => {
    setFormError(null);
    setDrafts((current) => ({ ...current, [entity]: next }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    for (const object of changed) {
      const problem = searchableProblem(drafts[object] ?? [], forms[object]);
      if (problem) {
        setEntity(object);
        setFormError(problem);
        return;
      }
    }
    setSaving(true);
    const done: FormEntity[] = [];
    const counts: string[] = [];
    try {
      for (const object of changed) {
        const fields = drafts[object] ?? [];
        try {
          await save.mutateAsync({ entity: object, tenantId, fields });
        } catch (err) {
          setEntity(object);
          const why = err instanceof ApiError ? err.message : "Please try again.";
          setFormError(
            done.length > 0
              ? `${done.map(entityLabel).join(" and ")} saved. The ${recordNoun(object)} fields could not be saved: ${why}`
              : `Could not save the searchable ${recordNoun(object)} fields: ${why}`,
          );
          return;
        }
        done.push(object);
        counts.push(`${entityLabel(object)}: ${fields.length} custom ${fields.length === 1 ? "field" : "fields"}`);
        // Saved: this object reads from its saved form again, unless its list changed since it was sent.
        setDrafts((current) => {
          if (current[object] !== fields) return current;
          const next = { ...current };
          delete next[object];
          return next;
        });
      }
      toast.success("Searchable fields saved", counts.join(" · "));
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };
  const close = (next: boolean) => {
    if (!saving) onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        variant="drawer"
        size="md"
        className="focus:outline-none"
        // Opened, the pane itself takes the focus rather than its first tab: a screen reader reads its title,
        // Tab goes on to the tabs and the rows, and no tab looks pressed before anything is chosen.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
        // Locked while it saves: no Escape, no click outside, and the close button out of reach.
        onEscapeKeyDown={(e) => saving && e.preventDefault()}
        onInteractOutside={(e) => saving && e.preventDefault()}
        closeClassName={saving ? "pointer-events-none opacity-40" : undefined}
      >
        <DiscardGuard changed={changed.length > 0 && !saving} />
        <DialogHeader>
          <DialogTitle>Edit searchable fields</DialogTitle>
          <DialogDescription>
            Choose what the search box looks in. Changes apply to everyone in this workspace, and each person
            only searches the fields their role can see.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={(e) => void submit(e)} noValidate>
          <DialogBody className="space-y-5">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <Tabs
              value={entity}
              onValueChange={(next) => {
                if (saving) return;
                setEntity(next as FormEntity);
                setFormError(null);
              }}
              className="space-y-5"
            >
              <div className="space-y-2">
                <TabsList aria-label="Object">
                  {OBJECTS.map((object) => {
                    const Icon = OBJECT_ICON[object];
                    return (
                      <TabsTrigger key={object} value={object} disabled={saving}>
                        <Icon aria-hidden />
                        {entityLabel(object)}
                        {/* The other object's changes are kept while this one is shown, and saved with it. */}
                        {changed.includes(object) && (
                          <>
                            <span className="size-1.5 rounded-full bg-accent-9" aria-hidden />
                            <span className="sr-only">(unsaved changes)</span>
                          </>
                        )}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                <p className="text-xs text-text-muted">{SEARCHED_IN[entity]}</p>
              </div>

              <TabsContent value={entity}>

              <section aria-labelledby={`${objectId}-fields`} className="space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 id={`${objectId}-fields`} className="text-xs font-medium text-text">
                    Searchable fields
                  </h3>
                  {form && (
                    <span className={full ? "text-xs font-medium tabular-nums text-warning-11" : "text-xs tabular-nums text-text-muted"}>
                      {picked.length} of {SEARCHABLE_MAX} custom fields
                    </span>
                  )}
                </div>

                <ul className="space-y-2">
                  {alwaysSearched(entity).map((name) => (
                    <li
                      key={name}
                      className="flex h-8 items-center gap-2 rounded-md border border-border bg-surface-sunken px-2.5 text-base text-text-muted"
                    >
                      <Lock className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{name}</span>
                      <span className="shrink-0 text-xs text-text-subtle">Always searched</span>
                    </li>
                  ))}

                  {!form ? (
                    design.error ? (
                      <li>
                        <Callout tone="danger">
                          Could not load the {noun} fields.{" "}
                          {design.error instanceof ApiError ? design.error.message : "Please try again."}{" "}
                          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => void design.refetch()}>
                            Try again
                          </Button>
                        </Callout>
                      </li>
                    ) : (
                      <li aria-busy="true" aria-label={`Loading the ${noun} fields`} className="space-y-2">
                        {[0, 1].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
                      </li>
                    )
                  ) : (
                    <>
                      {picked.map((key, index) => {
                        const choice = choices.get(key);
                        const title = choice?.field.title ?? key;
                        const problemId = `${objectId}-row-${index}-problem`;
                        return (
                          // Keyed by place: a row whose field changes stays the same row, and keeps the focus.
                          // eslint-disable-next-line react/no-array-index-key
                          <li key={index} className="space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="min-w-0 flex-1">
                                <SearchableFieldPicker
                                  label={`${entityLabel(entity)} fields`}
                                  triggerRef={(el) => {
                                    rowRefs.current[index] = el;
                                  }}
                                  disabled={saving}
                                  value={{
                                    key,
                                    title,
                                    detail: choice ? `${fieldTypeLabel(choice.field.type)} · ${choice.section.title}` : "",
                                  }}
                                  options={searchableOptions(form, picked.filter((k) => k !== key))}
                                  onChange={(next) => setPicked(replaceSearchable(picked, key, next))}
                                  invalid={Boolean(choice?.reason)}
                                  describedBy={choice?.reason ? problemId : undefined}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label={`Remove ${title}`}
                                title={`Remove ${title}`}
                                disabled={saving}
                                onClick={() => {
                                  setPicked(toggleSearchable(picked, key, false));
                                  setFocusRow(index);
                                }}
                              >
                                <X />
                              </Button>
                            </div>
                            {/* A field left searchable after its type changed: it can only be removed. */}
                            {choice?.reason && (
                              <p id={problemId} className="text-xs text-danger-11">
                                {choice.reason}. Remove it to save.
                              </p>
                            )}
                          </li>
                        );
                      })}

                      {!full && (
                        <li className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <SearchableFieldPicker
                              label={`${entityLabel(entity)} fields`}
                              triggerRef={blankRef}
                              value={null}
                              options={searchableOptions(form, picked)}
                              onChange={(key) => {
                                setPicked([...picked, key]);
                                // The sixth field takes the blank row's place: the focus goes to it.
                                if (picked.length + 1 >= SEARCHABLE_MAX) setFocusRow(picked.length);
                              }}
                              disabled={saving || offerable === 0}
                              placeholder={offerable === 0 ? `No ${noun} field can be searched yet` : "Add a field"}
                            />
                          </div>
                          {/* Keeps the blank row as wide as the others, under their remove buttons. */}
                          <span className="w-7 shrink-0" aria-hidden />
                        </li>
                      )}
                    </>
                  )}
                </ul>

                <p className="text-xs text-text-muted">
                  {form && offerable === 0 && picked.length === 0
                    ? `Create a text, email, phone, dropdown or multi-select ${noun} field on the Custom fields page first. `
                    : full
                      ? `${SEARCHABLE_MAX} is the most: remove a field to add another. `
                      : ""}
                  Searching finds what is typed in these fields, not their names, from 3 characters. Number, date,
                  yes/no and reference fields cannot be searched.
                </p>
              </section>
              </TabsContent>
            </Tabs>
          </DialogBody>

          <DialogFooter>
            {changed.length > 1 && (
              <span className="mr-auto self-center text-xs text-text-muted">
                Saves {changed.map(entityLabel).join(" and ")}
              </span>
            )}
            <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={saving} disabled={changed.length === 0}>
              Save
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
